import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import {
  Spinner, Flag, ArrowFatUp, ArrowFatDown, ProhibitInset, ArrowCounterClockwise,
  Quotes, FileText, User, CalendarBlank, CaretDown,
} from '@phosphor-icons/react';

const PRIORITY_META = {
  high: { label: 'High', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  low: { label: 'Low', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  ignore: { label: 'Ignored', color: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400' },
  none: { label: 'Unranked', color: 'bg-white text-[#6B7280] border-[#E5E7EB]', dot: 'bg-[#CBD5E1]' },
};

// Trusted factual record: every claim carries a fact_type so a reader instantly knows
// whether something is verified, merely alleged, or an AI inference.
const FACT_TYPE_META = {
  verified_fact: { label: 'Verified fact', color: 'bg-green-50 text-green-700 border-green-200' },
  documented_statement: { label: 'Documented statement', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  user_allegation: { label: 'Your allegation', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  third_party_allegation: { label: 'Other-party allegation', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  ai_inference: { label: 'AI inference', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  disputed_fact: { label: 'Disputed', color: 'bg-red-50 text-red-700 border-red-200' },
  unverified_recollection: { label: 'Unverified recollection', color: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  legal_interpretation: { label: 'Legal interpretation', color: 'bg-teal-50 text-teal-700 border-teal-200' },
};

// Claim status lifecycle: where each claim stands against the evidence.
const CLAIM_STATUS_META = {
  unreviewed: { label: 'Unreviewed', color: 'bg-white text-[#6B7280] border-[#E5E7EB]' },
  supported: { label: 'Supported', color: 'bg-green-50 text-green-700 border-green-200' },
  contradicted: { label: 'Contradicted', color: 'bg-red-50 text-red-700 border-red-200' },
  inconclusive: { label: 'Inconclusive', color: 'bg-amber-50 text-amber-700 border-amber-200' },
};
const CLAIM_STATUS_ORDER = ['unreviewed', 'supported', 'contradicted', 'inconclusive'];

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'high', label: 'High' },
  { id: 'low', label: 'Low' },
  { id: 'none', label: 'Unranked' },
  { id: 'ignore', label: 'Ignored' },
];

const PriorityButton = ({ active, onClick, color, icon: Icon, label, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center gap-1 px-2.5 py-1 text-xs font-ibm font-semibold rounded-md border transition-all ${active ? color : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
    <Icon size={12} weight={active ? 'fill' : 'regular'} /> {label}
  </button>
);

const ClaimStatusDropdown = ({ claim, onChange }) => {
  const [open, setOpen] = useState(false);
  const status = claim.claim_status || 'unreviewed';
  const meta = CLAIM_STATUS_META[status] || CLAIM_STATUS_META.unreviewed;
  return (
    <div className="relative">
      <button data-testid={`claim-status-btn-${claim._id}`} onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1 text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}>
        {meta.label} <CaretDown size={9} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1 w-40" data-testid={`claim-status-menu-${claim._id}`}>
            {CLAIM_STATUS_ORDER.map(s => (
              <button key={s} data-testid={`claim-status-opt-${s}-${claim._id}`}
                onClick={() => { setOpen(false); if (s !== status) onChange(claim._id, s); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-ibm hover:bg-[#F3F4F6] ${s === status ? 'font-semibold text-[#002FA7]' : 'text-[#6B7280]'}`}>
                {CLAIM_STATUS_META[s].label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ClaimCard = ({ claim, onSetPriority, onSetStatus }) => {
  const pr = claim.priority || 'none';
  const meta = PRIORITY_META[pr] || PRIORITY_META.none;
  const ft = FACT_TYPE_META[claim.fact_type] || FACT_TYPE_META.documented_statement;
  return (
    <div className={`bg-white border rounded-lg p-4 transition-all ${pr === 'ignore' ? 'opacity-60' : ''} ${pr === 'high' ? 'border-l-4 border-l-red-500 border-y-[#E5E7EB] border-r-[#E5E7EB]' : 'border-[#E5E7EB]'}`}
      data-testid={`claim-${claim._id}`}>
      <div className="flex items-start gap-2">
        <Quotes size={16} className="text-[#002FA7] mt-0.5 shrink-0" weight="fill" />
        <p className="text-sm font-ibm text-[#09090B] leading-relaxed flex-1">{claim.claim_text}</p>
        <div className="flex items-center gap-1.5 shrink-0">
          <ClaimStatusDropdown claim={claim} onChange={onSetStatus} />
          <span className={`text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`} data-testid={`claim-priority-badge-${claim._id}`}>
            {meta.label}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-[#6B7280] font-ibm pl-6">
        <span className={`px-1.5 py-0.5 rounded border font-bold text-[10px] uppercase tracking-wider ${ft.color}`} data-testid={`claim-facttype-${claim._id}`} title={claim.fact_basis || ''}>
          {ft.label}
        </span>
        {claim.claim_type && <span className="px-1.5 py-0.5 rounded bg-[#F3F4F6] uppercase tracking-wider font-semibold text-[10px]">{claim.claim_type}</span>}
        {claim.actor && <span className="flex items-center gap-1"><User size={11} /> {claim.actor}</span>}
        {claim.date_referenced && <span className="flex items-center gap-1"><CalendarBlank size={11} /> {claim.date_referenced}</span>}
        {claim.document_name && <span className="flex items-center gap-1"><FileText size={11} /> {claim.document_name}</span>}
      </div>
      {claim.fact_basis && <p className="text-[11px] text-[#94A3B8] font-ibm italic pl-6 mt-1">Basis: {claim.fact_basis}</p>}
      <div className="flex items-center gap-1.5 mt-3 pl-6 flex-wrap">
        <PriorityButton active={pr === 'high'} onClick={() => onSetPriority(claim._id, pr === 'high' ? 'none' : 'high')} color={PRIORITY_META.high.color} icon={ArrowFatUp} label="High" testid={`claim-high-${claim._id}`} />
        <PriorityButton active={pr === 'low'} onClick={() => onSetPriority(claim._id, pr === 'low' ? 'none' : 'low')} color={PRIORITY_META.low.color} icon={ArrowFatDown} label="Low" testid={`claim-low-${claim._id}`} />
        <PriorityButton active={pr === 'ignore'} onClick={() => onSetPriority(claim._id, pr === 'ignore' ? 'none' : 'ignore')} color={PRIORITY_META.ignore.color} icon={ProhibitInset} label="Ignore" testid={`claim-ignore-${claim._id}`} />
        {pr !== 'none' && (
          <button onClick={() => onSetPriority(claim._id, 'none')} data-testid={`claim-clear-${claim._id}`}
            className="flex items-center gap-1 px-2 py-1 text-xs font-ibm text-[#6B7280] hover:text-[#002FA7] transition-all">
            <ArrowCounterClockwise size={12} /> Clear
          </button>
        )}
      </div>
    </div>
  );
};

export const ClaimsTab = ({ caseId }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/claims`);
      setClaims(data.claims || []);
    } catch (err) {
      logger.error('failed to load claims:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const setPriority = async (claimId, priority) => {
    // optimistic update
    setClaims(prev => prev.map(c => (c._id === claimId ? { ...c, priority } : c)));
    try {
      await api.patch(`/cases/${caseId}/claims/${claimId}/priority`, { priority });
    } catch (err) {
      logger.error('failed to set priority:', err);
      load();
    }
  };

  const setStatus = async (claimId, status) => {
    setClaims(prev => prev.map(c => (c._id === claimId ? { ...c, claim_status: status } : c)));
    try {
      await api.patch(`/cases/${caseId}/claims/${claimId}/status`, { status });
    } catch (err) {
      logger.error('failed to set claim status:', err);
      load();
    }
  };

  const counts = claims.reduce((acc, c) => {
    const p = c.priority || 'none';
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});
  const filtered = filter === 'all' ? claims : claims.filter(c => (c.priority || 'none') === filter);

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="claims-tab"><Spinner size={28} className="animate-spin text-[#002FA7]" /></div>;
  }

  return (
    <div data-testid="claims-tab" className="space-y-5">
      <div>
        <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2">
          <Flag size={20} className="text-[#002FA7]" weight="fill" /> Claims
        </h3>
        <p className="text-sm text-[#6B7280] font-ibm mt-0.5">
          Every claim extracted from your documents. Mark each as <span className="font-semibold text-red-600">High</span> or <span className="font-semibold text-amber-600">Low</span> priority, or <span className="font-semibold">Ignore</span> it — and track where each stands: <span className="font-semibold text-green-700">Supported</span>, <span className="font-semibold text-red-700">Contradicted</span>, or <span className="font-semibold text-amber-700">Inconclusive</span>.
        </p>
      </div>

      {claims.length === 0 ? (
        <div className="text-center py-14 bg-white border border-dashed border-[#E5E7EB] rounded-lg" data-testid="claims-empty">
          <Quotes size={40} className="mx-auto text-[#002FA7]/40 mb-3" weight="fill" />
          <p className="text-[#6B7280] font-ibm">No claims yet. Upload and analyze documents — extracted claims will appear here for you to prioritize.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap" data-testid="claims-filters">
            {FILTERS.map(f => {
              const n = f.id === 'all' ? claims.length : (counts[f.id] || 0);
              return (
                <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`claims-filter-${f.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-ibm font-semibold rounded-full transition-all ${filter === f.id ? 'bg-[#002FA7] text-white' : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                  {f.label} <span className={`px-1.5 rounded-full text-[10px] ${filter === f.id ? 'bg-white/20' : 'bg-[#F3F4F6]'}`}>{n}</span>
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            {filtered.length === 0 ? (
              <p className="text-sm text-[#6B7280] font-ibm italic py-6 text-center">No claims in this category.</p>
            ) : (
              filtered.map(c => <ClaimCard key={c._id} claim={c} onSetPriority={setPriority} onSetStatus={setStatus} />)
            )}
          </div>
        </>
      )}
    </div>
  );
};
